import { Button } from "@weirgroup/weir-dls";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGetAssetAPI, useUpdateAssetAPI } from "../../../hooks/use-asset-api";
import { AssetOverviewFormData, useAssetOverviewForm } from "../../../hooks/use-asset-overview-form";
import { useDarkMode } from "../../../hooks/use-darkmode";
import { useDateConverter } from "../../../hooks/use-date-converter";
import { BucketDataType, TagType, UpdateAssetDataType } from "../../../types/bucketTypes";
import { getPermission, isReadOnly } from "../../../utils/roles.util";
import { ModifiedFormField } from "../../modified-form-field/modified-form-field";
import "../styles.scss";
import { useAnalyticsEvent } from "../../../hooks/useAnalyticsEvent";
import { LoadingSpinner } from "../../loading-spinner";

interface Props {
  assetStatus?: string;
  selectedAsset?: BucketDataType;
  tagData?: TagType;
  isGuest?: boolean;
}

export const AssetOverview = (props: Props) => {
  // Helper to map nulls to undefined for form fields
  const mapNullsToUndefined = (obj: any) => {
    const result: any = {};
    Object.keys(obj).forEach(key => {
      result[key] = obj[key] === null ? undefined : obj[key];
    });
    return result;
  };

  const {
    assetStatus,
    selectedAsset,
    tagData,
    isGuest,
  } = props;
  const logAnalyticsEvent = useAnalyticsEvent();
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const { convertDateStringToFormat } = useDateConverter();
  const { darkMode } = useDarkMode();
  const { t } = useTranslation();
  const isNonEsco = selectedAsset?.bucket_type?.toLowerCase() === 'non-esco';
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const getAssetAPIHook = useGetAssetAPI(selectedAsset?.asset_id);
  const { handleSubmit, control, reset, getValues, formState, setValue } = useAssetOverviewForm(getAssetAPIHook?.data, selectedAsset);
  const updateAssetAPIHook = useUpdateAssetAPI({
    onSuccess: () => {
      reset(getValues())
      setIsEditing(false);
    }
  });
  const isLoading = updateAssetAPIHook.isPending || getAssetAPIHook?.isLoading;

  useEffect(() => {
    if (assetStatus && assetStatus.toLowerCase() === 'active') {
      const mapped:UpdateAssetDataType = mapNullsToUndefined(selectedAsset);
      if (selectedAsset?.model !== undefined) {
        mapped.equipment_model = selectedAsset.model as string;
      }
      const editFormValues:UpdateAssetDataType = {
        asset_id: mapped.asset_id,
        stock_number: mapped.stock_number,
        machine_number: mapped.machine_number,
        attachment_type: mapped.attachment_type,
        equipment_model: mapped.equipment_model,
        capacity: mapped.capacity,
        manufacturer: mapped.manufacturer,
        make: mapped.make,
        class: mapped.class,
        lip_width: mapped.lip_width,
      }
      reset(editFormValues);
    }
  }, [assetStatus, selectedAsset, reset]);
  // Fetch latest asset data on mount
  useEffect(() => {
    const roles = sessionStorage.getItem('roles');
    const permissions = getPermission(roles)
    setIsDisabled(isReadOnly(permissions));
  }, []);
  const handleCancel = () => {
    setIsEditing(false)
    reset();
  }
  const toggleIsEditing = () => {
    setIsEditing((prev) => !prev);
  };
  const saveAssetInfo = (formData: AssetOverviewFormData) => {
    const { last_scan, ...updatedData } = formData;
    updateAssetAPIHook.mutate(updatedData);
  }

  useEffect(() => {
    setValue("last_scan", convertDateStringToFormat(tagData?.location?.created_at ?? ""));
  }, [tagData?.location?.created_at, convertDateStringToFormat, setValue]);

  const isEditField = (field: keyof UpdateAssetDataType) => {
    const isEditMode = isEditing && !isDisabled && !isLoading;
    const onlyEscoAssetFields: Array<keyof UpdateAssetDataType> = ["machine_number", "stock_number"];
    if (onlyEscoAssetFields.includes(field) && isEditMode) return true;
    if (!onlyEscoAssetFields.includes(field) && isEditMode && isNonEsco) return true;
    return false
  };
  return (
    <div className="asset-overview" data-testid="asset-overview">
      {isLoading ?
            <LoadingSpinner darkMode={darkMode} />
        : <></>}
      <div className="tag-id-container">
        <strong className="tag-id" style={{ color: darkMode ? "white" : "rgb(105, 118, 128)" }}>{t("tagID")}:</strong>
        <p className="tag-id-value" style={{ color: darkMode ? "white" : "black" }}>{tagData?.esn}</p>
      </div>
      {assetStatus && assetStatus.toLowerCase() !== 'updated' && (
        <div className="tag-id-container">
          <strong className="tag-id" style={{ color: darkMode ? "white" : "rgb(105, 118, 128)" }}>{t("assetStatus")}:</strong>
          <p className="tag-id-value" style={{ color: darkMode ? "white" : "black" }}>{assetStatus}</p>
        </div>
      )}
      <div className="flex-container ">
        <div className="flex-item">
          <ModifiedFormField disabled={!isEditField("stock_number")} name="stock_number" control={control} label={t("assetOverviewField.stockNumber")} />
          <ModifiedFormField optionalText={t("required")} disabled={!isEditField("attachment_type")} name="attachment_type" control={control} label={t("attachmentType")} />
          <ModifiedFormField optionalText={t("required")} disabled={!isEditField("equipment_model")} name="equipment_model" control={control} label={t("assetOverviewField.model")} />
          <ModifiedFormField disabled={!isEditField("capacity")} name="capacity" control={control} label={t("assetOverviewField.capacity")} />
          <ModifiedFormField optionalText={t("required")} disabled={!isEditField("manufacturer")} name="manufacturer" control={control} label={t("assetOverviewField.manufacturer")} />
        </div>
        <div className="flex-item">
          <ModifiedFormField disabled={!isEditField("machine_number")} name="machine_number" control={control} label={t("assetOverviewField.machineNumber")} />
          <ModifiedFormField optionalText={t("required")} disabled={!isEditField("make")} name="make" control={control} label={t("assetOverviewField.make")} />
          <ModifiedFormField disabled={!isEditField("class")} name="class" control={control} label={t("assetOverviewField.class")} />
          <ModifiedFormField disabled={!isEditField("lip_width")} name="lip_width" control={control} label={t("assetOverviewField.lipWidth")} />
          <ModifiedFormField fullWidth disabled name="last_scan" control={control} label={t("assetOverviewField.lastScan")} />
        </div>
      </div>
      <div data-testid="buttons">
        {
          isEditing ?
            <div className="buttons" >
              <Button
                darkMode={darkMode}
                className="cancel-btn"
                label={"Cancel"}
                size={"sm"}
                variant={"secondary"}
                disabled={isDisabled || isLoading}
                onClick={handleCancel}
              />
              <Button
                darkMode={darkMode}
                label={"Save"}
                size={"sm"}
                variant={"primary"}
                disabled={!formState.isValid || isLoading}
                onClick={()=>{
                  logAnalyticsEvent('web_user_edited_asset_info', { description: 'User edited asset info', assetId: selectedAsset?.asset_id });
                  handleSubmit(saveAssetInfo)()}
                }
              />
            </div>
            :
            <div className="edit-btn">
              <Button
                darkMode={darkMode}
                label={"Edit Asset Info"}
                size={"sm"}
                variant={"secondary"}
                disabled={isDisabled || isGuest || isLoading}
                onClick={toggleIsEditing}
              />
            </div>
        }
      </div>
    </div>
  );
};
